// API utility functions
class ApiService {
  private baseUrl: string
  
  constructor() {
    this.baseUrl = '/api'
  }

  // Get authentication headers
  private getAuthHeaders(): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    }
    const token = localStorage.getItem('auth_token')
    const guestSessionId = localStorage.getItem('guest_session_id')
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    } else if (guestSessionId) {
      headers['X-Guest-Session-Id'] = guestSessionId
    }
    return headers
  }

  // Generic API request method
  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`
    try {
      const response = await fetch(url, {
        ...options,
        headers: {
          ...this.getAuthHeaders(),
          ...options.headers
        }
      })
      const data = await response.json()
      if (!response.ok) {
        throw new Error(data.error || `HTTP ${response.status}`)
      }
      return data
    } catch (error) {
      console.error('API request failed:', error)
      throw error
    }
  }

  // File upload method with better error handling
  async uploadFile(file: File): Promise<{
    success: boolean
    data: {
      fileId: string
      originalName: string
      filename: string
      mimetype: string
      size: number
      url: string
      metadata?: {
        width: number
        height: number
        format: string
        channels: number
        hasAlpha: boolean
      }
      uploadedAt: string
    }
  }> {
    const maxSize = 10 * 1024 * 1024
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/heic', 'image/heif', 'image/avif', 'image/tiff', 'image/tif']
    const allowedExtensions = ['heic', 'heif', 'avif', 'tiff', 'tif']
    if (file.size > maxSize) throw new Error('File too large. Maximum size is 10MB.')
    const hasValidType = allowedTypes.includes(file.type) || allowedExtensions.some(ext => file.name.toLowerCase().endsWith(`.${ext}`))
    if (!hasValidType) throw new Error('Unsupported file format. Please upload JPEG, PNG, WebP, HEIC, AVIF, or TIFF images.')

    const formData = new FormData()
    formData.append('image', file)
    const headers: Record<string, string> = {}
    const token = localStorage.getItem('auth_token')
    const guestSessionId = localStorage.getItem('guest_session_id')
    if (token) headers['Authorization'] = `Bearer ${token}`
    else if (guestSessionId) headers['X-Guest-Session-Id'] = guestSessionId

    try {
      const response = await fetch(`${this.baseUrl}/upload`, { method: 'POST', headers, body: formData })
      const data = await response.json()
      if (!response.ok) throw new Error(data.error || `Upload failed with status ${response.status}`)
      return data
    } catch (error) {
      console.error('Upload failed:', error)
      throw error
    }
  }

  // Get file info
  async getFile(fileId: string) {
    return this.request(`/upload/${fileId}`)
  }

  // List user files
  async listFiles() {
    return this.request('/upload')
  }

  // Delete file
  async deleteFile(fileId: string) {
    return this.request(`/upload/${fileId}`, { method: 'DELETE' })
  }

  // Caption generation methods
  async generateCaptions(fileId: string | null, styles: string[], userContext?: {
    mood?: string
    platform?: string
    keywords?: string
    cocoSsdAnalysis?: {
      keywords: string[]
      detections: Array<{ class: string; score: number; bbox: number[] }>
      confidence: number
    }
    regenerationLevel?: number
    length?: string
  }) {
    return this.request('/captions/generate', {
      method: 'POST',
      body: JSON.stringify({ fileId, styles, userContext })
    })
  }

  async getCaptionStyles() {
    return this.request('/captions/styles')
  }

  async getUserProfile() {
    return this.request('/user/profile')
  }

  async getUserStats() {
    return this.request('/user/stats')
  }

  // Health check
  async healthCheck() {
    return this.request('/health')
  }
}

export const apiService = new ApiService()

// Upload progress tracking
export interface UploadProgress {
  loaded: number
  total: number
  percentage: number
}

export function uploadFileWithProgress(
  file: File,
  onProgress?: (progress: UploadProgress) => void
): Promise<any> {
  return new Promise((resolve, reject) => {
    console.log('Starting XMLHttpRequest upload with progress tracking')

    // Validate file before upload
    const maxSize = 10 * 1024 * 1024 // 10MB
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/heic', 'image/heif', 'image/avif', 'image/tiff', 'image/tif']
    const allowedExtensions = ['heic', 'heif', 'avif', 'tiff', 'tif']
    
    if (file.size > maxSize) {
      reject(new Error('File too large. Maximum size is 10MB.'))
      return
    }
    
    const hasValidType = allowedTypes.includes(file.type) || allowedExtensions.some(ext => file.name.toLowerCase().endsWith(`.${ext}`))
    if (!hasValidType) {
      reject(new Error('Unsupported file format. Please upload JPEG, PNG, WebP, HEIC, AVIF, or TIFF images.'))
      return
    }

    const xhr = new XMLHttpRequest()
    const formData = new FormData()
    formData.append('image', file)

    // Set up progress tracking
    if (onProgress) {
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          const progress: UploadProgress = {
            loaded: event.loaded,
            total: event.total,
            percentage: Math.round((event.loaded / event.total) * 100)
          }
          onProgress(progress)
        }
      })
    }

    // Set up response handling
    xhr.addEventListener('load', () => {
      console.log('XMLHttpRequest completed with status:', xhr.status)
      
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const response = JSON.parse(xhr.responseText)
          console.log('XMLHttpRequest success response:', response)
          resolve(response)
        } catch (error) {
          console.error('Failed to parse response JSON:', error)
          reject(new Error('Invalid JSON response'))
        }
      } else {
        try {
          const errorResponse = JSON.parse(xhr.responseText)
          console.error('XMLHttpRequest error response:', errorResponse)
          reject(new Error(errorResponse.error || `HTTP ${xhr.status}`))
        } catch (error) {
          console.error('XMLHttpRequest failed with status:', xhr.status)
          reject(new Error(`HTTP ${xhr.status}`))
        }
      }
    })

    xhr.addEventListener('error', () => {
      console.error('XMLHttpRequest network error')
      reject(new Error('Network error'))
    })

    xhr.addEventListener('abort', () => {
      console.error('XMLHttpRequest aborted')
      reject(new Error('Upload aborted'))
    })

    // Always use relative /api path — nginx proxies it to port 8000
    const url = `/api/upload`
    console.log('Opening XMLHttpRequest to:', url)
    xhr.open('POST', url)

    // Set headers AFTER xhr.open()
    const token = localStorage.getItem('auth_token')
    const guestSessionId = localStorage.getItem('guest_session_id')

    if (token) {
      xhr.setRequestHeader('Authorization', `Bearer ${token}`)
      console.log('Set Authorization header')
    } else if (guestSessionId) {
      xhr.setRequestHeader('X-Guest-Session-Id', guestSessionId)
      console.log('Set Guest-Session-Id header')
    }

    // Start upload
    console.log('Starting XMLHttpRequest upload')
    xhr.send(formData)
  })
}